/*
 * Copyright © 2025 AionTrack. All rights reserved.
 */

import { supabase } from '@/lib/supabase'
import type {
  ProjectMember,
  ProjectMemberWithProfile,
  ProjectInvitation,
  CreateProjectMemberInput,
  UpdateProjectMemberInput,
  CreateProjectInvitationInput,
} from '@/types/project-teams'

// =============================================
// PROJECT MEMBERS API FUNCTIONS
// =============================================

/**
 * Get all members of a project (including the owner)
 */
export async function getProjectMembers(
  projectId: string
): Promise<ProjectMemberWithProfile[]> {
  // Fetch project members
  const { data, error } = await supabase
    .from('project_members')
    .select(`
      *,
      profile:profiles!project_members_user_id_fkey(*),
      invited_by_profile:profiles!project_members_invited_by_fkey(*)
    `)
    .eq('project_id', projectId)
    .order('joined_at', { ascending: false })

  if (error) {
    console.error('Error fetching project members:', error)
    throw error
  }

  const members = data || []

  // Also fetch the project owner and add them to the list
  const { data: project, error: projectError } = await supabase
    .from('projects')
    .select('owner_user_id')
    .eq('id', projectId)
    .maybeSingle()

  if (projectError) {
    console.error('Error fetching project owner:', projectError)
  } else if (project?.owner_user_id) {
    // Check if owner is already in members list
    const ownerAlreadyMember = members.some((m: any) => m.user_id === project.owner_user_id)

    if (!ownerAlreadyMember) {
      // Fetch owner's profile
      const { data: ownerProfile, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', project.owner_user_id)
        .maybeSingle()

      if (!profileError && ownerProfile) {
        // Add owner as a synthetic member with 'admin' role
        members.unshift({
          project_id: projectId,
          user_id: project.owner_user_id,
          role: 'admin', // Owner has admin privileges
          invited_at: new Date().toISOString(),
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          profile: ownerProfile,
        } as any)
      }
    }
  }

  return members
}

/**
 * Get current user's membership in a project
 */
export async function getProjectMembership(
  projectId: string
): Promise<ProjectMember | null> {
  const { data: { user } } = await supabase.auth.getUser()

  if (!user) {
    return null
  }

  const { data, error } = await supabase
    .from('project_members')
    .select('*')
    .eq('project_id', projectId)
    .eq('user_id', user.id)
    .maybeSingle()

  if (error) {
    console.error('Error fetching project membership:', error)
    return null
  }

  return data
}

/**
 * Add a member to a project
 */
export async function addProjectMember(
  input: CreateProjectMemberInput
): Promise<ProjectMember> {
  const { data, error } = await supabase
    .from('project_members')
    .insert({
      ...input,
      joined_at: new Date().toISOString(),
    })
    .select()
    .maybeSingle()

  if (error) {
    console.error('Error adding project member:', error)
    throw error
  }

  if (!data) {
    throw new Error('Failed to add project member - permission denied or member already exists')
  }

  return data
}

/**
 * Update a project member's role
 */
export async function updateProjectMember(
  projectId: string,
  userId: string,
  input: UpdateProjectMemberInput
): Promise<ProjectMember> {
  const { data, error } = await supabase
    .from('project_members')
    .update(input)
    .eq('project_id', projectId)
    .eq('user_id', userId)
    .select()
    .maybeSingle()

  if (error) {
    console.error('Error updating project member:', error)
    throw error
  }

  if (!data) {
    throw new Error('Project membership not found or update not allowed')
  }

  return data
}

/**
 * Remove a member from a project
 */
export async function removeProjectMember(
  projectId: string,
  userId: string
): Promise<void> {
  const { error } = await supabase
    .from('project_members')
    .delete()
    .eq('project_id', projectId)
    .eq('user_id', userId)

  if (error) {
    console.error('Error removing project member:', error)
    throw error
  }
}

// =============================================
// PROJECT INVITATIONS API FUNCTIONS
// =============================================

/**
 * Get all pending invitations for a project
 */
export async function getProjectInvitations(
  projectId: string
): Promise<ProjectInvitation[]> {
  const { data, error } = await supabase
    .from('project_invitations')
    .select('*')
    .eq('project_id', projectId)
    .is('accepted_at', null)
    .gt('expires_at', new Date().toISOString())
    .order('created_at', { ascending: false })

  if (error) {
    console.error('Error fetching project invitations:', error)
    throw error
  }

  return data || []
}

/**
 * Create a project invitation via backend API
 * Returns invitation with URL for manual sharing if email fails
 */
export async function createProjectInvitation(
  input: CreateProjectInvitationInput
): Promise<ProjectInvitation & { invitation_url: string; email_sent: boolean }> {
  const { data: { session } } = await supabase.auth.getSession()

  if (!session) {
    throw new Error('Not authenticated')
  }

  const BACKEND_URL = import.meta.env.VITE_BACKEND_URL || 'http://localhost:8000'
  const url = `${BACKEND_URL}/api/projects/${input.project_id}/invitations`

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${session.access_token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      email: input.email,
      role: input.role,
      expires_in_days: input.expires_in_days || 7,
    }),
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ detail: 'Failed to create invitation' }))
    throw new Error(errorData.detail || 'Failed to create invitation')
  }

  const data = await response.json()

  // Fetch the full invitation record from database to match expected return type
  const { data: invitation, error } = await supabase
    .from('project_invitations')
    .select('*')
    .eq('id', data.invitation_id)
    .maybeSingle()

  if (error) {
    console.error('Error fetching created invitation:', error)
    throw error
  }

  if (!invitation) {
    throw new Error('Invitation created but could not be retrieved')
  }

  // Return invitation with additional fields from API response
  return {
    ...invitation,
    invitation_url: data.invitation_url,
    email_sent: data.email_sent
  }
}

/**
 * Accept a project invitation
 */
export async function acceptProjectInvitation(token: string): Promise<string> {
  const { data, error } = await supabase.rpc('accept_project_invitation', {
    invitation_token: token,
  })

  if (error) {
    console.error('Error accepting project invitation:', error)
    throw new Error(error.message || 'Failed to accept invitation')
  }

  if (!data) {
    throw new Error('No project ID returned from invitation acceptance')
  }

  return data as string // Returns project_id
}

/**
 * Revoke a project invitation
 */
export async function revokeProjectInvitation(invitationId: string): Promise<void> {
  const { error } = await supabase
    .from('project_invitations')
    .delete()
    .eq('id', invitationId)

  if (error) {
    console.error('Error revoking project invitation:', error)
    throw error
  }
}

/**
 * Get invitations for current user's email
 */
export async function getMyInvitations(): Promise<ProjectInvitation[]> {
  const { data: { user } } = await supabase.auth.getUser()

  if (!user?.email) {
    return []
  }

  const { data, error } = await supabase
    .from('project_invitations')
    .select(`
      *,
      project:projects(*)
    `)
    .eq('email', user.email)
    .is('accepted_at', null)
    .gt('expires_at', new Date().toISOString())
    .order('created_at', { ascending: false })

  if (error) {
    console.error('Error fetching user invitations:', error)
    throw error
  }

  return data || []
}

/**
 * Check if user is project owner or admin
 */
export async function isProjectAdmin(projectId: string, userId?: string): Promise<boolean> {
  // If no userId provided, get current user
  if (!userId) {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) {
      return false
    }
    userId = user.id
  }

  const { data, error } = await supabase.rpc('is_project_admin', {
    p_project_id: projectId,
    p_user_id: userId,
  })

  if (error) {
    console.error('Error checking project admin status:', error)
    return false
  }

  return data as boolean
}
